/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * MobileModuleSwitchPicker
 *
 * Three clickable thumbnail cards for mobile module switcher display style.
 * Only shown when user has multiple modules (showSwitcher === true).
 */

import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { useLayoutStore } from '@/stores/layoutStore';
import { useModuleNavigation } from '@/hooks/useModuleNavigation';
import type { MobileModuleSwitchStyle } from '@/components/layouts/mobile-bottom-nav/types';

const OPTIONS: { value: MobileModuleSwitchStyle; previewId: string }[] = [
  { value: 'tab', previewId: 'tab' },
  { value: 'header', previewId: 'header' },
  { value: 'fab', previewId: 'fab' },
];

export function MobileModuleSwitchPicker() {
  const { t } = useTranslation('common');
  const { mobileModuleSwitchStyle, setMobileModuleSwitchStyle } = useLayoutStore();
  const { showSwitcher } = useModuleNavigation();

  if (!showSwitcher) return null;

  return (
    <div className="mmsp-container">
      <div className="mmsp-header">
        <h3 className="mmsp-title">
          {t('mobileModuleSwitchSettings.title', 'Mobile Module Switcher')}
        </h3>
        <p className="mmsp-description">
          {t('mobileModuleSwitchSettings.description', 'Choose how to switch between modules on mobile')}
        </p>
      </div>

      <div className="mmsp-grid">
        {OPTIONS.map(({ value, previewId }) => {
          const isSelected = mobileModuleSwitchStyle === value;
          return (
            <button
              key={value}
              className={`mmsp-card ${isSelected ? 'selected' : ''}`}
              onClick={() => setMobileModuleSwitchStyle(value)}
              type="button"
            >
              <div className="mmsp-preview">
                <SwitcherPreview type={previewId} />
              </div>
              <div className="mmsp-label">
                <span className="mmsp-name">
                  {t(`mobileModuleSwitchSettings.${value}.name`, value)}
                </span>
                <span className="mmsp-desc">
                  {t(`mobileModuleSwitchSettings.${value}.description`, '')}
                </span>
              </div>
              {isSelected && (
                <div className="mmsp-check">
                  <Check size={14} />
                </div>
              )}
            </button>
          );
        })}
      </div>

      <style>{styles}</style>
    </div>
  );
}

function SwitcherPreview({ type }: { type: string }) {
  if (type === 'header') {
    return (
      <div className="mmsp-mini mmsp-mini-header">
        <div className="mmsp-mini-hdr-bar">
          <div className="mmsp-mini-pill" />
          <div className="mmsp-mini-spacer" />
          <div className="mmsp-mini-dot" />
        </div>
        <div className="mmsp-mini-content" />
        <div className="mmsp-mini-bar">
          <div className="mmsp-mini-tab" />
          <div className="mmsp-mini-tab active" />
          <div className="mmsp-mini-tab" />
        </div>
      </div>
    );
  }

  if (type === 'fab') {
    return (
      <div className="mmsp-mini mmsp-mini-fab">
        <div className="mmsp-mini-content" />
        <div className="mmsp-mini-fab-area">
          <div className="mmsp-mini-fab-btn" />
        </div>
        <div className="mmsp-mini-bar">
          <div className="mmsp-mini-tab active" />
          <div className="mmsp-mini-tab" />
          <div className="mmsp-mini-tab" />
        </div>
      </div>
    );
  }

  // tab (default)
  return (
    <div className="mmsp-mini mmsp-mini-tab-preview">
      <div className="mmsp-mini-content" />
      <div className="mmsp-mini-bar">
        <div className="mmsp-mini-mod-tab" />
        <div className="mmsp-mini-tab active" />
        <div className="mmsp-mini-tab" />
        <div className="mmsp-mini-tab" />
      </div>
    </div>
  );
}

const styles = `
  .mmsp-container {
    margin-bottom: 24px;
  }

  .mmsp-header {
    margin-bottom: 16px;
  }

  .mmsp-title {
    font-size: 14px;
    font-weight: 600;
    color: var(--color-text-primary);
    margin: 0 0 4px;
  }

  .mmsp-description {
    font-size: 12px;
    color: var(--color-text-muted);
    margin: 0;
  }

  .mmsp-grid {
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    gap: 12px;
  }

  @media (max-width: 640px) {
    .mmsp-grid {
      grid-template-columns: 1fr;
    }
  }

  .mmsp-card {
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 10px;
    padding: 12px;
    background: var(--color-bg-primary);
    border: 2px solid var(--color-border-subtle);
    border-radius: 10px;
    cursor: pointer;
    transition: all 0.15s ease;
    text-align: left;
  }

  .mmsp-card:hover {
    border-color: var(--color-border-default);
    background: var(--color-bg-hover);
  }

  .mmsp-card.selected {
    border-color: var(--color-brand);
    box-shadow: 0 0 0 1px var(--color-brand);
  }

  .mmsp-check {
    position: absolute;
    top: 8px;
    right: 8px;
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background: var(--color-brand);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .mmsp-label {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .mmsp-name {
    font-size: 12px;
    font-weight: 600;
    color: var(--color-text-primary);
  }

  .mmsp-desc {
    font-size: 10px;
    color: var(--color-text-muted);
    line-height: 1.3;
  }

  .mmsp-preview {
    height: 64px;
    border-radius: 6px;
    overflow: hidden;
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
  }

  .mmsp-mini {
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
  }

  .mmsp-mini-content {
    flex: 1;
    background: var(--color-bg-primary);
  }

  /* Shared mini bar */
  .mmsp-mini-bar {
    display: flex;
    align-items: center;
    justify-content: space-around;
    padding: 4px 8px;
    border-top: 1px solid var(--color-border-subtle);
    background: var(--color-bg-secondary);
  }

  .mmsp-mini-tab {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: var(--color-border-default);
  }

  .mmsp-mini-tab.active {
    background: var(--color-brand);
  }

  /* Tab preview: module tab as colored square */
  .mmsp-mini-mod-tab {
    width: 8px;
    height: 8px;
    border-radius: 2px;
    background: #dc2626;
  }

  /* Header preview */
  .mmsp-mini-hdr-bar {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 3px 6px;
    border-bottom: 1px solid var(--color-border-subtle);
    background: var(--color-bg-secondary);
  }

  .mmsp-mini-pill {
    width: 20px;
    height: 6px;
    border-radius: 3px;
    background: #dc2626;
    opacity: 0.6;
  }

  .mmsp-mini-spacer {
    flex: 1;
  }

  .mmsp-mini-dot {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--color-border-default);
  }

  /* FAB preview */
  .mmsp-mini-fab-area {
    position: relative;
    height: 0;
  }

  .mmsp-mini-fab-btn {
    position: absolute;
    bottom: 2px;
    right: 6px;
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: #dc2626;
  }
`;
